import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  getKnowledgeQuerySpuGroup,
  KnowledgeQuerySpuGroup,
  productList,
} from 'services/api/conversation';
import { ApiQueryKey } from 'services/config/queryKey';
import { formatLoggerData, logger } from 'services/logger';
import { toUpperCase } from 'utils/toLowerCase';

export interface QAPairProduct {
  item: Conversation.Product;
  skuGroups: KnowledgeQuerySpuGroup[];
}

interface QueryQAPairProductParams {
  enable: boolean;
  storeId?: string;
  itemId?: string;
  channel: General.Platform | General.PlatformUpperCase;
  region: General.Region | General.UpperCaseRegion;
  selectedSkus?: string[];
  onQueryProduct?: (product?: Conversation.Product) => void;
}

export const useQueryQAPairProduct = (params: QueryQAPairProductParams) => {
  const { enable, storeId, itemId, channel, region, selectedSkus, onQueryProduct } = params;

  const [defaultApplyType, setDefaultApplyType] = useState<'link' | 'sku'>();

  const { data: product, isLoading } = useQuery({
    enabled: !!storeId && !!itemId && enable,
    queryKey: [ApiQueryKey.QAPairProduct, { storeId, itemId }],
    queryFn: async () => {
      const startTime1 = Date.now();
      logger.info(
        `[useQueryQAPairProduct] productList ${formatLoggerData({
          storeId,
          itemId,
        })} startTime: ${startTime1}`,
      );
      const { itemList } = await productList({
        page_no: 1,
        page_size: 1,
        internal_store_id: storeId!,
        external_item_ids: itemId,
      });
      const item = itemList?.[0];
      const endTime1 = Date.now();
      logger.info(
        `[useQueryQAPairProduct] productList ${formatLoggerData({
          storeId,
          itemId,
          item,
        })} endTime: ${endTime1} duration: ${endTime1 - startTime1}`,
      );
      if (item) {
        const { externalShopId } = item;
        const startTime2 = Date.now();
        logger.info(
          `[useQueryQAPairProduct] getKnowledgeQuerySpuGroup ${formatLoggerData({
            externalSpuId: itemId!,
            externalStoreId: externalShopId,
            platform: toUpperCase(channel) as General.PlatformUpperCase,
            region: toUpperCase(region) as General.UpperCaseRegion,
          })} startTime: ${startTime2}`,
        );
        const spuGroupResponse = await getKnowledgeQuerySpuGroup({
          externalSpuId: itemId!,
          externalStoreId: externalShopId,
          platform: toUpperCase(channel) as General.PlatformUpperCase,
          region: toUpperCase(region) as General.UpperCaseRegion,
        });
        const endTime2 = Date.now();
        logger.info(
          `[useQueryQAPairProduct] getKnowledgeQuerySpuGroup ${formatLoggerData({
            externalSpuId: itemId!,
            externalStoreId: externalShopId,
            platform: toUpperCase(channel) as General.PlatformUpperCase,
            region: toUpperCase(region) as General.UpperCaseRegion,
          })} endTime: ${endTime2} duration: ${endTime2 - startTime2} totalDuration: ${
            endTime2 - startTime1
          }`,
        );
        return {
          item,
          skuGroups: spuGroupResponse?.data?.skuGroups ?? [],
        };
      }
      onQueryProduct?.(item);
    },
    staleTime: 3 * 60 * 1000,
  });

  const selectable = useMemo(() => {
    if (!product) {
      return false;
    }

    const { skuGroups, item } = product;

    // 存在多个上架中的SKU，且这些SKU属于多个SKU组
    const existMultipleSku = item.skuInfo.length > 1;
    if (!existMultipleSku) {
      return false;
    }
    // 构建 SKU 到 SKU 组的映射
    const mapping = skuGroups.reduce((acc, spu) => {
      spu.skuMappings.forEach((mapping) => {
        const realSkuKey = mapping.skuKey.split('|')?.[0];
        if (realSkuKey) {
          acc[realSkuKey] = [...(acc[realSkuKey] || []), mapping.skuGroupId];
        }
      });
      return acc;
    }, {} as Record<string, string[]>);
    // 收集所有 SKU 对应的 SKU 组
    const skuGroupsSize = new Set<string>();
    item.skuInfo.forEach((sku) => {
      const groups = mapping[sku.skuId] || [];
      groups.forEach((groupId) => skuGroupsSize.add(groupId));
    });
    return skuGroupsSize.size > 1;
  }, [product]);

  useEffect(() => {
    if (product) {
      const { skuInfo } = product.item;
      const isApplyAllSku =
        !selectable || selectedSkus?.length === 0 || selectedSkus?.length === skuInfo.length;
      if (isApplyAllSku) {
        setDefaultApplyType('link');
      } else {
        setDefaultApplyType('sku');
      }
    }
  }, [product, selectedSkus, selectable]);

  return useMemo(() => {
    return { isLoading, product, selectable, defaultApplyType };
  }, [isLoading, product, selectable, defaultApplyType]);
};
